import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import { SkipErrorReporting } from '../request-options.ts';

/**
 * PATCH /people/:id/contribution-account — reorder the person's
 * contribution accounts. Body is the legacy JSON:API shape the server
 * expects (singular `person` type with snake_case attribute key).
 */
export function updateContributionAccountRequest(
  personId: string,
  accountIds: string[]
): RequestInfo {
  const opts: QueryUrlOptions = {
    identifier: { type: 'people' },
    op: 'query',
    resourcePath: 'people',
  };
  return {
    url: `${buildBaseURL(opts)}/${personId}/contribution-account`,
    method: 'PATCH',
    body: JSON.stringify({
      data: {
        type: 'person',
        id: personId,
        attributes: { account_order: accountIds },
      },
    }),
    cacheOptions: { [SkipCache]: true },
  };
}

/** A single alert from GET /people/:id/slr-alerts. */
export interface SlrAlert {
  type: string;
  category: 'account-setup' | 'account-ineligible' | 'plan-requirement' | 'default';
  display_mode: 'single' | 'grouped';
  resolution_type: 'upload' | 'dropdown' | 'contact-support' | 'none';
  title: string;
  description: string;
  priority: number;
  metadata: Record<string, unknown>;
}

/** Response from GET /people/:id/slr-alerts. */
export interface SlrAlertsResponse {
  page_state: string;
  alerts: SlrAlert[];
  contribution_account: {
    id: string;
    servicer_name: string | null;
    account_number: string | null;
    balance: number | null;
    is_pif: boolean;
    servicer_address_type: string | null;
    /** PARTIALLY_APPLIED means the account was created but no servicer match was found */
    status?: string;
  } | null;
}

/**
 * GET /people/:id/slr-alerts — student-loan-repayment alerts for a person.
 * Returns a plain JSON payload (not JSON:API); the caller casts the response
 * `content` to {@link SlrAlertsResponse}.
 */
export function getSlrAlertsRequest(personId: string): RequestInfo {
  const opts: QueryUrlOptions = {
    identifier: { type: 'people' },
    op: 'query',
    resourcePath: 'people',
  };
  return {
    url: `${buildBaseURL(opts)}/${personId}/slr-alerts`,
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
    // Non-critical fetch whose failures must not surface globally (no
    // "Access denied" toast from the response handler): every call site
    // handles failure locally — fail-quiet [] on the dashboard widget,
    // outage notice on the reimagined pages. See SkipErrorReporting docs.
    options: {
      [SkipErrorReporting]: true,
    },
  };
}
